import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flowsApi } from '@/services/flows.api';

type CopyPayload = { name: string; description?: string };

/** The workspace's templates, plus the three ways a template comes or goes. */
export function useTemplates(enabled = true) {
  const queryClient = useQueryClient();
  // Templates are flows, so their list lives under the 'flows' key: anything
  // that refreshes flows (rename, trash, a copy) refreshes templates too.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['flows'] });

  const templatesQuery = useQuery({
    queryKey: ['flows', 'templates'],
    queryFn: () => flowsApi.getTemplates(),
    enabled,
    retry: false,
  });

  const startFromTemplate = useMutation({
    mutationFn: ({ id, ...payload }: CopyPayload & { id: string }) => flowsApi.useTemplate(id, payload),
    onSuccess: invalidate,
  });

  const saveAsTemplate = useMutation({
    mutationFn: ({ id, ...payload }: CopyPayload & { id: string }) => flowsApi.saveAsTemplate(id, payload),
    onSuccess: invalidate,
  });

  const createTemplate = useMutation({
    mutationFn: (payload: CopyPayload) => flowsApi.createTemplate(payload),
    onSuccess: invalidate,
  });

  return {
    templates: templatesQuery.data ?? [],
    isLoading: templatesQuery.isLoading,
    startFromTemplate,
    saveAsTemplate,
    createTemplate,
  };
}
